import { FC, useRef, useState } from 'react'
import styled from 'styled-components'

import { useSubscribeToClickOutsideEvent } from '../../hooks'
import { Text } from '..'
import ChevronDownIcon from '@tuhi/components/assets/images/chevronDown.svg'

const Container = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`

const DropdownContainer = styled.div`
    display: flex;
    flex-direction: column;
    position: relative;
    width: 230px;
`

const DropdownButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 36px;
    width: 230px;
    padding: 0 16px;
    border-radius: 8px;
    border: 1px solid ${(p) => p.theme.colors.tertiary.tertiary200};
    background-color: ${(p) => p.theme.colors.white};
    color: ${(p) => p.theme.colors.tertiary.tertiary900};
    z-index: 1;
    gap: 8px;

    &:disabled {
        cursor: not-allowed;
        pointer-events: none;

        border: 1px solid ${(p) => p.theme.colors.tertiary.tertiary200};
        color: ${(p) => p.theme.colors.tertiary.tertiary600};
        background-color: ${(p) => p.theme.colors.tertiary.tertiary100};
    }

    > svg {
        overflow: visible;
    }
`

const DropdownButtonText = styled(Text)`
    text-align: left;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
`

const OptionsContainer = styled.div`
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    border-radius: 8px;
    border: 1px solid ${(p) => p.theme.colors.tertiary.tertiary200};

    background-color: white;
    overflow-y: auto;
    max-height: 245px;

    /* ACA-140 (Jon round 3): the open menu must overlay the fields below it.
       At z-index 1 it tied with the next field's control (also z-index 1) and
       later DOM painted on top, hiding the menu. Lift it clear of siblings. */
    z-index: 30;
`

const OptionsList = styled.div`
    display: flex;
    flex-direction: column;
`

const Option = styled.button`
    background-color: transparent;
    box-sizing: border-box;
    border: none;
    cursor: pointer;
    height: 36px;

    text-align: left;
    padding: 0 16px;

    &:hover {
        background-color: ${(p) => p.theme.colors.primaryGrey};
    }
`

export interface IDropdownOption {
    id: string
    text: string
}

interface Props {
    label?: string
    options: IDropdownOption[]
    multiSelect?: boolean
    defaultOption?: IDropdownOption
    disabled?: boolean
    onSelect: (id: string, selectedOption: IDropdownOption) => void
    /**
     * Stable test handle. When supplied, three derived ids are emitted:
     *   `<testId>`          the trigger button
     *   `<testId>-menu`     the open options list
     *   `<testId>-option`   each option (with data-option-id carrying the option's id)
     * Omitted by default so nothing is forced on existing call sites.
     * Convention: .kiro/steering/ui-test-selectors.md
     */
    testId?: string
}

export const Dropdown: FC<Props> = ({
    label,
    disabled,
    options,
    defaultOption,
    onSelect,
    testId,
}) => {
    const [openMenu, setOpenMenu] = useState(false)
    const ref = useRef(null)

    useSubscribeToClickOutsideEvent(ref, () => {
        setOpenMenu(false)
    })

    return (
        <Container>
            <Text size="medium" $weight="bold">
                {label}
            </Text>

            <DropdownContainer ref={ref} onFocus={() => setOpenMenu(true)}>
                <DropdownButton disabled={disabled} data-testid={testId}>
                    <DropdownButtonText
                        size="xSmall"
                        $weight="regular"
                        color="none"
                    >
                        {defaultOption?.text}
                    </DropdownButtonText>
                    <ChevronDownIcon />
                </DropdownButton>

                {options.length > 0 && openMenu && (
                    <OptionsContainer
                        data-testid={testId ? `${testId}-menu` : undefined}
                    >
                        <OptionsList>
                            {options.map((option) => (
                                <Option
                                    key={option.id}
                                    data-testid={
                                        testId ? `${testId}-option` : undefined
                                    }
                                    data-option-id={option.id}
                                    onClick={() => {
                                        setOpenMenu(false)
                                        if (defaultOption?.id === option.id)
                                            return

                                        onSelect(
                                            option.id,
                                            JSON.parse(JSON.stringify(option))
                                        )
                                    }}
                                >
                                    <Text size="xxSmall" $weight="regular">
                                        {option.text}
                                    </Text>
                                </Option>
                            ))}
                        </OptionsList>
                    </OptionsContainer>
                )}
            </DropdownContainer>
        </Container>
    )
}
